import { NetworkType, zeroExWrapperUrl } from 'abis/set/sets'
import { atom } from 'jotai'
import { ARTXAI, JMX } from 'products'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_APY_INFO } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { RequestError } from 'utils/errors'
import { Address, isAddressEqual, zeroAddress } from 'viem'
import { z } from 'zod'
import { ApyData, parseApyResponse, RawApy } from './model'

const ResponseSchema = z.object({
  data: z.object({
    apy: z.number(),
    assetsApy: z.record(z.number()),
  }),
})

export const requestApy = async (chain: NetworkType, address: Address) => {
  if (
    isAddressEqual(address as Address, JMX.address) ||
    isAddressEqual(address as Address, ARTXAI.address)
  ) {
    return { data: { apy: 0, assetsApy: { [zeroAddress]: 0 } } }
  }
  const url = `${zeroExWrapperUrl}/apy/${chain}/${address}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
  const resp = await response.json()

  return resp as RawApy
}

export const apyQuery = async <T>({
  queryKey: [key, productAddress, chainId],
}: {
  queryKey: [string, Address, NetworkType]
}) => {
  const res = await requestApy(chainId, productAddress)

  if (!res || !res.data) {
    throw new Error(
      `Fails in apyQuery function with a key ${key}, additional info: ${(res as unknown as { message: unknown }).message}`,
    )
  }
  const response = ResponseSchema.parse(res)
  if (!response || !response.data) throw new RequestError()
  return parseApyResponse<T>(response.data)
}

export const apyRequestFactory = () => {
  const indexAddressAtom = atom<Address>(zeroAddress)
  const indexNetworkAtom = atom<NetworkType>(NetworkType.Mainnet)

  const mainProperties = createAtomsWithQuery<
    ApyData,
    Address,
    NetworkType,
    null,
    [string, Address, NetworkType]
  >(
    { first: indexAddressAtom, second: indexNetworkAtom },
    QUERY_KEY_APY_INFO,
    apyQuery<ApyData>,
    DEFAULT_REFETCH_INTERVAL,
    'apyQuery',
  )

  return { chainId: indexNetworkAtom, productAddress: indexAddressAtom, ...mainProperties }
}
